/**
 * **テーマの演出 → 時間の仕組みの型の仕様**（Issue #529。索引 #525）。**`duelEditor/` の外**。
 *
 * `editorPorts/duelRenderer.js` が「フレームを舞台の層にする」ものだとすると、こちらは
 * **「演出を `SourcePort` の型にする」**もの。両方の語彙を知ってよいのはこの層だけ
 * （`duelEditor/` の舞台は時間の仕組みを知らず、`duelEditor/timeline` はドメインを知らない）。
 *
 * ## ここが ocg の語彙を翻訳する
 *
 * 演出（`themes/<theme>/effects/<event>.js`）は **`REASON_*` / `LOCATION_*` の数値を知らない**
 * （設計文書 §1.3）。生のイベントから**意味のある名前**を付けるのはここの仕事で、
 * 演出が受け取るのは整えた `payload` だけ。
 *
 * ## 演出は自分のイベントしか見ない
 *
 * コアの生成器は「型ごとに全アンカーを走査する」作りなので、**走査はこの層がやる**。
 * 演出には**自分のイベント 1 つ**と、過去だけを引く口（`history`）を渡す（設計文書 §3.3）。
 */
import { boardAfterIndex } from './boardTimeline';
import {
  checkBoardCommit, effectDuration, effectFor, roleTypeId,
} from '../duelEditor/core/effect';
import { CATALOG, checkThemeSituations, situate } from './duelSituations';
import { toDuelBoard } from './duelBoard';
import { zoneKey } from '../duelEditor/core/field/zones';

/** ocg の場所の番号 → ゾーンの種類（`duelEditor/core/field/zones.js` の語彙） */
const LOCATION_KIND = Object.freeze({
  1: 'maindeck', 2: 'hand', 4: 'monster', 8: 'magic_trap',
  16: 'grave', 32: 'removed', 64: 'extra',
});

/** モンスター / 魔法罠だけが列を持つ（他は 1 枠） */
const ROW_KINDS = Object.freeze(['monster', 'magic_trap']);

/** ocg の `loc`（`{ controller, location, sequence }`）→ ゾーンのキー。分からなければ null */
export function locZone(loc) {
  const kind = LOCATION_KIND[loc?.location];
  if (!kind) return null;
  const player = loc?.controller === 1 ? 1 : 0;
  // 魔法罠の 6 枠目はフィールド魔法（`duelBoard.js` の写し替えと同じ規則）
  if (kind === 'magic_trap' && loc?.sequence === 5) return zoneKey('field', player);
  return ROW_KINDS.includes(kind) ? zoneKey(kind, player, loc?.sequence ?? 0) : zoneKey(kind, player);
}

/**
 * ocg のフェイズ → 見せる文字（`components/ocgInput/common.js` の `PHASE_LABEL` と同じ語彙）。
 * **演出は ocg の綴りを知らない**ので、翻訳はここで済ませる。
 */
export const PHASE_LABEL = Object.freeze({
  draw: 'DRAW PHASE', standby: 'STANDBY PHASE', main1: 'MAIN PHASE 1',
  battle_start: 'BATTLE PHASE', battle_step: 'BATTLE PHASE', damage: 'BATTLE PHASE',
  damage_calc: 'BATTLE PHASE', battle: 'BATTLE PHASE', main2: 'MAIN PHASE 2', end: 'END PHASE',
});

/**
 * **カードが動いた理由**（`MSG_MOVE` の `reason`。`ocgcore/service/constants.py` の `REASON_*` と同じ値）。**演出はこの数値を知らない** ——
 * 意味の名前（`isMaterial` など）に翻訳してから渡す。
 */
export const REASON = Object.freeze({
  DESTROY: 0x1, RELEASE: 0x2, MATERIAL: 0x8, SUMMON: 0x10, BATTLE: 0x20, EFFECT: 0x40,
  COST: 0x80, ADJUST: 0x100, RULE: 0x400, DISCARD: 0x4000, FUSION: 0x40000, SYNCHRO: 0x80000,
  RITUAL: 0x100000, XYZ: 0x200000, LINK: 0x10000000,
});

/** 召喚素材の種類（`move` の理由から。**演出はこの名前だけを読む**） */
function materialKindOf(reason) {
  if (reason & REASON.SYNCHRO) return 'synchro';
  if (reason & REASON.FUSION) return 'fusion';
  if (reason & REASON.RITUAL) return 'ritual';
  if (reason & REASON.XYZ) return 'xyz';
  if (reason & REASON.LINK) return 'link';
  return null;
}

/** 召喚素材として動いたか（シンクロ・融合・儀式の素材。`_materialSummon.js` と同じ見分け方） */
const MATERIAL_REASONS = REASON.MATERIAL | REASON.FUSION | REASON.SYNCHRO;

/** カードの表示形式（`POS_*`。1 表攻撃 / 2 裏攻撃 / 4 表守備 / 8 裏守備）→ 意味の名前 */
const positionOf = (pos) => (Number.isFinite(pos) && pos > 0
  ? Object.freeze({ faceUp: Boolean(pos & 0x5), defense: Boolean(pos & 0xc) })
  : null);

/** 戦闘のカード（`attack` / `battle`）。`battle` は ATK / DEF と破壊されたかも持つ */
function battler(data, key) {
  const c = data?.[key];
  if (!c) return null;
  const n = (v) => (Number.isFinite(v) ? v : null);
  return Object.freeze({
    uid: c.uid ?? null,
    zone: locZone(c.loc),
    player: c.loc?.controller === 1 ? 1 : 0,
    atk: n(data[`${key}_atk`]),
    def: n(data[`${key}_def`]),
    destroyed: Boolean(data[`${key}_destroyed`]),
  });
}

/** `data.card` は object（`{ uid, loc }`）のことも**文字列の uid** のこともある（`move` は文字列） */
const cardOf = (data) => (typeof data?.card === 'string' ? { uid: data.card } : (data?.card ?? {}));

/**
 * 生のイベント → **意味のある名前の payload**。
 *
 * 共通のものだけをここで付け、イベントごとの細かい意味は `data` をそのまま渡す
 * （**演出が `data.card.loc.controller` を読み始めたら翻訳が足りていない**合図）。
 *
 * **`move` は形が違う**（Issue #533）—— `data.card` が文字列の uid で、場所は `from` / `to` に入る。
 * ここを取り違えると `move`（1 試合で 59 件。素材・墓地送り・バウンスの全部）の uid が落ちる。
 */
export function duelPayload(event) {
  const data = event?.data ?? {};
  const card = cardOf(data);
  const toLoc = data.to?.loc ?? card.loc ?? data.loc ?? null;
  const fromLoc = data.from?.loc ?? null;
  const reason = data.reason ?? 0;
  return Object.freeze({
    type: event?.type ?? null,
    uid: card.uid ?? data.uid ?? null,
    code: data.code ?? card.code ?? null,
    /** 行き先（＝そのイベントの後の居場所）。`move` 以外はそのカードの場所 */
    zone: locZone(toLoc),
    /** 発動したときの場所（`chaining` の `data.trigger`） */
    triggerZone: locZone(data.trigger?.loc ?? null),
    /** 出どころ（`move` だけ。素材がどこから来たか） */
    fromZone: locZone(fromLoc),
    reason,
    /** 召喚素材として動いたか（演出は `REASON_*` の数値を知らない） */
    isMaterial: Boolean(reason & MATERIAL_REASONS),
    /** 攻撃するカードと対象（`attack` / `battle`。対象が無ければ直接攻撃） */
    attacker: battler(data, 'attacker'),
    target: battler(data, 'target'),
    /**
     * **複数枚を持つイベントの一覧**（`become_target` / `select_card` など。`data.cards`）。
     * 1 イベントから複数の絵を出すのはこれ ＋ 宣言の `for`（設計文書 §0.1）。
     */
    /*
     * `draw` / `shuffle_hand` は `cards` が**uid の文字列**の配列で、絵は `codes` に並ぶ（Issue #542）
     */
    cards: Object.freeze((data.cards ?? []).map((c, i) => (typeof c === 'string'
      ? Object.freeze({ uid: c, code: data.codes?.[i] ?? null, zone: null, player: data.player === 1 ? 1 : 0 })
      : Object.freeze({
        uid: c?.uid ?? null,
        code: c?.code ?? null,
        zone: locZone(c?.loc ?? c),
        player: c?.controller ?? c?.loc?.controller ?? 0,
        // ステータスの変化（疑似イベント `stat_change`。`editorPorts/statChanges.js`。Issue #584）の前後と元々の値
        ...(c?.from && c?.to ? { from: Object.freeze({ ...c.from }), to: Object.freeze({ ...c.to }), base: Object.freeze({ ...(c.base ?? {}) }) } : {}),
      })))),
    /*
     * **誰のイベントか**。`damage` / `recover` / `pay_lpcost` / `draw` / `win` などは `data.player` が
     * 当事者（以前は場所 → ターンプレイヤーの順で引いていて、**ダメージが相手の LP に飛んでいた**。Issue #542）
     */
    player: (Number.isFinite(data.player) ? data.player : null)
      ?? toLoc?.controller ?? fromLoc?.controller ?? event?.turn_player ?? 0,
    /** LP の増減（`damage` / `recover` / `pay_lpcost` の `value`） */
    amount: Number.isFinite(data.value) ? data.value : null,
    /** 勝者（`win`） */
    winner: Number.isFinite(data.winner) ? data.winner : null,
    /** 行き先 / 出どころの**ゾーンの種類**（`grave` / `removed` / `hand` / `maindeck` / `extra` / `monster` / `magic_trap`） */
    toKind: LOCATION_KIND[toLoc?.location] ?? null,
    fromKind: LOCATION_KIND[fromLoc?.location] ?? null,
    /** 動いた理由（`REASON_*` を意味の名前に）。演出はこれを読む */
    isDestroy: Boolean(reason & REASON.DESTROY),
    isBattle: Boolean(reason & REASON.BATTLE),
    isEffect: Boolean(reason & REASON.EFFECT),
    isCost: Boolean(reason & REASON.COST),
    isRelease: Boolean(reason & REASON.RELEASE),
    isDiscard: Boolean(reason & REASON.DISCARD),
    /** 規則による移動（発動し終えた魔法・罠が墓地へ など。Issue #556） */
    isRule: Boolean(reason & REASON.RULE),
    /** 表示形式（そのイベントの後 / `pos_change` の前） */
    position: positionOf(toLoc?.position ?? data.position),
    prevPosition: positionOf(data.prev_position),
    /** 積まれているチェーンの数と、無効にされたリンク */
    chainCount: (event?.chain_stack ?? []).length,
    /** 素材の種類（`synchro` / `fusion` / `ritual` / `xyz` / `link`。素材でなければ null） */
    materialKind: materialKindOf(reason),
    /**
     * **そのチェーンのリンク**（`chain_solving` / `chain_solved` / `chain_negated` は `data.chain` の番号しか
     * 持たないので、積まれている列から引く。`{ chain, code, uid, player, negated, disabled }`。
     * `negated` = 発動の無効（`chain_negated`）/ `disabled` = 効果の無効（`chain_disabled`）。演出で見せ方が違うので分けて持つ）
     */
    link: (() => {
      const l = (event?.chain_stack ?? []).find((x) => x?.chain === data.chain);
      return l ? Object.freeze({
        chain: l.chain, code: l.code ?? null, uid: l.card ?? null,
        player: l.trigger?.controller === 1 ? 1 : 0, negated: Boolean(l.negated), disabled: Boolean(l.disabled),
      }) : null;
    })(),
    /**
     * **積まれているリンクの全部**（チェーンの番号の順。`{ chain, code, uid, player, negated, disabled }`。
     * `player` は発動したプレイヤー＝発動したときの場所のコントローラー）。`chained` がチェーンの山を描くのに使う
     */
    chainLinks: Object.freeze([...(event?.chain_stack ?? [])]
      .filter((l) => Number.isFinite(l?.chain))
      .sort((a, b) => a.chain - b.chain)
      .map((l) => Object.freeze({
        chain: l.chain, code: l.code ?? null, uid: l.card ?? null,
        player: l.trigger?.controller === 1 ? 1 : 0, negated: Boolean(l.negated), disabled: Boolean(l.disabled),
      }))),
    /** 無効にされたリンクの番号（発動の無効・効果の無効のどちらも） */
    negatedChains: Object.freeze((event?.chain_stack ?? []).filter((l) => l?.negated || l?.disabled).map((l) => l.chain)),
    turnPlayer: event?.turn_player ?? 0,
    /*
     * **`phase` / `turn` / `turn_player` はイベントの「外枠」に載っている**（`data` の中ではない。
     * `editorPorts/hudState.js` の `ev.phase` と同じ）。`data.phase` は**番号**なので、
     * そちらを読むと `PHASE_LABEL` に当たらず「1」のような文字が出る（Issue #531 で踏んだ）。
     */
    /** チェーンの番号（`chaining` / `chain_solving` / `chain_negated`） */
    chain: data.chain ?? null,
    phase: event?.phase ?? null,
    phaseLabel: event?.phase ? (PHASE_LABEL[event.phase] ?? String(event.phase).toUpperCase()) : null,
    turn: event?.turn ?? 0,
    data,
  });
}

/** 召喚の成功 → その宣言（成功の `data` は空。**文脈は翻訳で持たせる**。Issue #578） */
const SUMMON_PAIR = Object.freeze({ summoned: 'summoning', spsummoned: 'spsummoning', flipsummoned: 'flipsummoning' });

/**
 * **行の一覧 → payload の一覧**（古い順）。1 行ずつは `duelPayload` で、**前後の文脈が要るものだけ**ここで付ける。
 *
 * - **召喚の成功に宣言のカードを付ける**（Issue #578）: `summoned` / `spsummoned` / `flipsummoned` の `data` は空
 *   （MSG_SUMMONED は 0 byte）なので、`summon: { uid, code, zone, player, position }` に**同じフェイズの中で直前の**
 *   `summoning` / `spsummoning` / `flipsummoning` を入れる（`new_phase` / `new_turn` をまたがない。無ければ null）。
 *   **窓は応答（op）では駄目** —— 通常召喚の宣言と成功は別の応答で来る（実データで op 303 → 305）。
 *   演出は `history` を読まず payload だけで組む（Issue #564 の方針）。サービスの `chain_stack` と同じやり方
 */
export function duelPayloads(rows) {
  const last = {};
  return (rows ?? []).map((r) => {
    const p = duelPayload(r?.event);
    const { type } = p;
    if (type === 'new_phase' || type === 'new_turn') {
      Object.keys(last).forEach((k) => { delete last[k]; });
      return p;
    }
    if (type === 'summoning' || type === 'spsummoning' || type === 'flipsummoning') {
      last[type] = Object.freeze({ uid: p.uid, code: p.code, zone: p.zone, player: p.player, position: p.position });
      return p;
    }
    const from = SUMMON_PAIR[type];
    return from ? Object.freeze({ ...p, summon: last[from] ?? null }) : p;
  });
}

/**
 * **過去だけを引く口**（設計文書 §3.3）。**窓（`until` / `within`）は必須** ——
 * 窓なしで `last('battle')` を引くと、効果ダメージのときに だいぶ前の `battle` が引かれる。
 *
 * @param rows 整列前の行（古い順）
 * @param index 自分のイベントの位置（**自分より前だけ**を見る）
 */
/**
 * **イベントの直後の盤面を引く**（Issue #533）。
 *
 * `ctx.board` は `editorPorts/boardTimeline.js` の**時系列**なので、そのままでは演出に渡せない
 * （演出が受け取るのは `duelEditor/core/board` の「ゾーンのキー → 置いてあるもの」）。
 * 索引で引いて写し替える。**同じ索引は 1 回だけ写す**（1 試合 967 イベント × 演出の本数ぶん走るので）。
 */
export function makeBoardLookup(timeline) {
  const cache = new Map();
  return (index) => {
    if (!Number.isFinite(index) || index < 0) return toDuelBoard(null, null);
    if (!cache.has(index)) cache.set(index, toDuelBoard(boardAfterIndex(index, timeline), null));
    return cache.get(index);
  };
}

export function makeHistory(rows, index, payloads, boardAt = () => toDuelBoard(null, null)) {
  const scopeOf = (scope, where) => {
    if (!scope || typeof scope !== 'object') throw new Error(`history.${where}: 窓（until / within）が要ります`);
    const hasUntil = Array.isArray(scope.until);
    const hasWithin = typeof scope.within === 'string';
    if (hasUntil === hasWithin) throw new Error(`history.${where}: until か within のどちらか一方です`);
    return scope;
  };
  /** その行で窓が閉じるか */
  const closes = (scope, row) => {
    if (Array.isArray(scope.until)) return scope.until.includes(row?.event?.type);
    if (scope.within === 'op') return (row?.groupId ?? null) !== (rows[index]?.groupId ?? null);
    if (scope.within === 'phase') return row?.event?.type === 'new_phase' || row?.event?.type === 'new_turn';
    return row?.event?.type === 'new_turn';
  };
  const entry = (i) => ({ event: rows[i]?.event?.type ?? null, payload: payloads[i], index: i });
  const walk = (event, scope, all) => {
    const out = [];
    for (let i = index - 1; i >= 0; i -= 1) {
      if (closes(scope, rows[i])) break;
      if (rows[i]?.event?.type !== event) continue;
      if (typeof scope.filter === 'function' && !scope.filter(payloads[i])) continue;
      out.push(entry(i));
      if (!all) break;
    }
    return out;
  };
  return {
    last: (event, scope) => walk(event, scopeOf(scope, 'last'), false)[0] ?? null,
    // **古い順**に返す（走査は後ろからなので反転する）
    run: (event, scope) => Object.freeze(walk(event, scopeOf(scope, 'run'), true).reverse()),
    /** 過去のそのイベントの**直後**の盤面（`entry` は `last` / `run` が返したもの） */
    boardAfter: (entry) => boardAt(rows[entry?.index]?.event?.index ?? entry?.index ?? -1),
  };
}

/**
 * **演出が宣言した「盤面を進める時刻」**（Issue #566。口 `board` → `{ at }`）。帯の頭からの ms を
 * `[0, 尺]` に丸めて返す。宣言が無い・null を返したら null（＝帯の頭で進む）。形が違えば例外。
 */
export function boardCommitMs(eff, ctx, durationMs) {
  if (typeof eff?.board !== 'function') return null;
  const out = eff.board(ctx);
  checkBoardCommit(out, `effect ${eff.situation}: board`);
  if (out == null) return null;
  const { at } = out;
  const span = Number.isFinite(durationMs) ? Math.max(0, durationMs) : Infinity;
  return Math.min(Math.max(0, at), span);
}

/**
 * 演出 1 つ → 型の仕様（**描き方は持たない**。`duelEditor/timeline/types.js::defineType` が例外にする）。
 *
 * **型は状況 × 役割ごとに 1 つ**（Issue #628）。演出は core のイベントを知らないので、どの core のイベントから
 * その状況が出るかはカタログ（`duelSituations/index.js::CATALOG`）で引き、行ごとに分岐レイヤー（`situate`）を通して
 * その状況になった行だけで帯を作る。
 *
 * `build` を使うのは、**尺が帯ごとに変わりうる**（`effect.duration(ctx)`）から ——
 * `opens` の経路は静的な `timing.durationMs` しか持てない。
 */
export function duelTypeSpec(effect, pick = null) {
  /*
   * **どのテーマの演出で組むか**（Issue #541）。`pick(situation, role)` を渡すと**今選んでいるテーマ**の演出で
   * `opens` / 尺 / SE を決める（無ければ帯を作らない）。渡さなければ `effect` そのもの。
   * 以前は一覧の**先に出たテーマが勝ち**で、2 つ目以降のテーマの `opens` と尺が使われなかった。
   */
  const { situation } = effect;
  // **役割**（motion / effect）。motion は型 `<situation>_motion` の別の帯になる
  const role = effect.role ?? 'effect';
  const current = () => (typeof pick === 'function' ? pick(situation, role) : effect);
  const coreEvent = CATALOG[situation]?.event ?? null;
  return {
    id: roleTypeId(situation, role),
    label: role === 'motion' ? `${effect.label ?? situation}（motion）` : (effect.label ?? situation),
    track: 'effect',
    layer: 'event',
    color: effect.color ?? null,
    // 尺は帯ごとに決まる（`build` が入れる）。ここは帯にならなかったときの逃げ道
    timing: { durationMs: effectDuration(effect, { payload: {} }) },
    build: (ctx) => {
      // テーマのファイルは**行ごと**に状況で引く（状況ごとのファイルしか無いテーマもある）
      const rows = ctx?.rows ?? ctx?.anchors ?? [];
      const payloads = duelPayloads(rows);
      const boardAt = makeBoardLookup(ctx?.board);
      const out = [];
      rows.forEach((row, i) => {
        if (!coreEvent || row?.event?.type !== coreEvent) return;
        const at = row?.event?.index ?? i;
        const raw = {
          payload: payloads[i],
          // **そのイベントが反映された後**と**反映される前**（素材がまだ場に居る盤面など）
          board: boardAt(at),
          boardBefore: boardAt(at - 1),
          history: makeHistory(rows, i, payloads, boardAt),
          objects: { byMark: () => [], get: () => null },   // 段階 4b
        };
        /*
         * **分岐レイヤーが状況と事実を付ける**（Issue #589 / #628）。null なら見せる状況ではない（どのテーマでも帯を作らない）。
         * この型の状況になった行だけ、テーマの `opens`（好みで出さない・絵にだけ要る値を足す）を通す。
         * `situation` は分岐レイヤーが決めたものを保つ（テーマの `opens` が落としても引き直せるように）
         */
        const situated = situate(coreEvent, raw);
        if (!situated || situated.situation !== situation) return;
        const eff = current();
        if (!eff) return;
        const ec = { ...raw, payload: situated };
        const opened = typeof eff.opens === 'function' ? eff.opens(ec) : situated;
        if (!opened) return;
        const data = { ...opened, situation };
        /*
         * **尺は 自己申告 → 宣言からの計測 → 既定**（Issue #532）。
         * 宣言だけで書いた演出は `duration` を省ける（最後のキーの ms が尺）。
         */
        const durationMs = effectDuration(eff, { ...ec, payload: data });
        /*
         * **帯ごとの重ね方**（Issue #556）。演出の `overlap` → コアの `overlapKey` / `overlaps`
         * （`duelEditor/timeline/align.js` の sequential が直前の帯の鍵で引く）。返さなければ型の id・重ねない
         */
        const ov = typeof eff.overlap === 'function' ? eff.overlap({ ...ec, payload: data }) : null;
        /*
         * **盤面をこのイベントの後へ進める時刻**（Issue #566）。演出の `board` → コアの `commitMs`
         * （帯の頭からの ms。尺を超えれば帯の終わりに丸める）。返さなければ帯の頭で進む（今までどおり）
         */
        const commitMs = boardCommitMs(eff, { ...ec, payload: data }, durationMs);
        out.push({
          anchor: row, data, durationMs,
          ...(ov?.key ? { overlapKey: ov.key } : {}),
          ...(ov?.after ? { overlaps: { ...ov.after } } : {}),
          ...(commitMs != null ? { commitMs } : {}),
        });
      });
      return out;
    },
    sounds: (data, item) => {
      const eff = current();
      return typeof eff?.sounds === 'function' ? (eff.sounds({ payload: data, item }) ?? []) : [];
    },
  };
}

/**
 * テーマ（1 つ、または一覧）の演出 → 型の仕様。
 *
 * **一覧を渡すと同じイベントは 1 つにまとめる**（先に出たテーマが勝つ）。型が持つのは**規則**だけで、
 * 絵はテーマごとに `EffectLayer` が引き直すので、**そのテーマにファイルが無ければ帯があっても描かれない**
 * （「テーマで出さない ＝ ファイルを置かない」がそのまま効く）。
 */
export function duelSpecs(theme, { pick = null } = {}) {
  const themes = Array.isArray(theme) ? theme : [theme];
  const seen = new Set();
  const out = [];
  themes.forEach((t) => {
    // **カタログに無い状況を名乗ったファイルは起動時に落とす**（Issue #589 / #628。「置いたのに出ない」を黙らせない）
    checkThemeSituations(t);
    // pick が無ければ**そのテーマの中**で引く
    const pickOf = typeof pick === 'function' ? pick : (situation, role) => effectFor(t?.effects ?? [], situation, role);
    (t?.effects ?? []).forEach((e) => {
      // 型は状況 × 役割ごとに 1 つ
      const id = roleTypeId(e.situation, e.role ?? 'effect');
      if (seen.has(id)) return;
      seen.add(id);
      out.push(duelTypeSpec(e, pickOf));
    });
  });
  /*
   * **motion の型を先に登録する**。同じ状況の帯は型の登録順に並び、整列（sequential）はその順に重ならないよう置くので、
   * motion（カードを動かす）→ effect（強調）の順になる
   */
  return [...out.filter((s) => s.id.endsWith('_motion')), ...out.filter((s) => !s.id.endsWith('_motion'))];
}

/**
 * **今選んでいるテーマの演出を引く口**（Issue #541）。型の一覧は全テーマの和で 1 回だけ作り
 * （editor の registry は作り直せない）、中身はこの口で**そのとき**のテーマから引く。
 *
 * @param getTheme 今のテーマを返す関数（ページの ref）
 */
export const themePicker = (getTheme) => (situation, role = 'effect') => effectFor(getTheme()?.effects ?? [], situation, role);

/**
 * **状態チャンネル（`fx_objects` / `fx_scene`）に渡す context**（Issue #541）。
 *
 * `places` / `clears` / `scene` の関数も `opens` と同じ `board` / `boardBefore` / `history` を受け取る
 * （以前はページが `() => ({})` を渡していて `{payload}` しか無かった）。
 * 帯は `source.anchorKey` で自分の行を引く。**同じ (rows, ctx) なら組み立ては 1 回**。
 */
export function duelEffectContext() {
  let memo = null;
  return (clip, { anchors, ctx } = {}) => {
    const rows = anchors ?? [];
    if (!memo || memo.rows !== rows || memo.ctx !== ctx) {
      memo = {
        rows,
        ctx,
        payloads: duelPayloads(rows),
        boardAt: makeBoardLookup(ctx?.board),
        indexByKey: new Map(rows.map((r, i) => [r?.key, i])),
      };
    }
    const i = memo.indexByKey.get(clip?.source?.anchorKey);
    if (i == null) return {};
    const at = rows[i]?.event?.index ?? i;
    return {
      board: memo.boardAt(at),
      boardBefore: memo.boardAt(at - 1),
      history: makeHistory(rows, i, memo.payloads, memo.boardAt),
      objects: { byMark: () => [], get: () => null },
    };
  };
}
